import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "../lib/api";
import { PageHeader, fmtNum, SelisihBadge } from "../components/Shared";
import { toast } from "sonner";
import {
  Search, Loader2, Scale, ChevronDown, ChevronRight, ChevronLeft, FileSpreadsheet, FileText,
  MapPin, CheckCircle2, CircleDashed,
} from "lucide-react";

const PAGE = 50;

const FILTERS = [
  { key: "", label: "Semua" },
  { key: "complete", label: "Lengkap Dihitung" },
  { key: "incomplete", label: "Belum Lengkap" },
  { key: "diff", label: "Ada Selisih" },
  { key: "minus", label: "Selisih Minus (-)" },
  { key: "plus", label: "Selisih Plus (+)" },
];

export default function SaldoMaster() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({}); // id_barang -> rows|null(loading)
  const [busy, setBusy] = useState("");

  const load = useCallback(async (p, s, st) => {
    setLoading(true);
    try {
      const { data } = await api.get("/master-saldo", { params: { search: s, status: st, skip: p * PAGE, limit: PAGE } });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setPage(0); load(0, search, status); }, 300);
    return () => clearTimeout(t);
  }, [search, status, load]);

  const toggleExpand = async (idb) => {
    if (expanded[idb] !== undefined) {
      setExpanded((e) => { const n = { ...e }; delete n[idb]; return n; });
      return;
    }
    setExpanded((e) => ({ ...e, [idb]: null }));
    try {
      const { data } = await api.get("/master-saldo/detail", { params: { id_barang: idb } });
      setExpanded((e) => ({ ...e, [idb]: data }));
    } catch (err) { toast.error("Gagal memuat rincian"); }
  };

  const doExport = async (type) => {
    setBusy(type);
    try {
      const res = await api.get("/export/saldo", { params: { status, type }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = type === "excel" ? "Laporan_Selisih_Saldo.xlsx" : "Laporan_Selisih_Saldo.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan saldo diunduh");
    } catch (e) { toast.error("Gagal mengekspor"); }
    finally { setBusy(""); }
  };

  const totalPages = Math.ceil(total / PAGE);

  return (
    <div className="pb-12">
      <PageHeader title="Saldo Master" subtitle="Total qty fisik semua tempat vs Qty Awal — digabung per ID Barang">
        <button onClick={() => doExport("excel")} disabled={busy} data-testid="saldo-export-excel" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
          {busy === "excel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel
        </button>
        <button onClick={() => doExport("pdf")} disabled={busy} data-testid="saldo-export-pdf" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
          {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF
        </button>
      </PageHeader>

      <div className="px-4 sm:px-6 lg:px-8 pt-6 space-y-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            data-testid="saldo-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nama / ID barang..."
            className="w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`saldo-filter-${f.key || "all"}`}
              onClick={() => setStatus(f.key)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                status === f.key
                  ? f.key === "minus" ? "bg-red-600 text-white" : "bg-teal-700 text-white"
                  : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                  {["", "Nama Obat", "ID Barang", "Satuan", "Total Qty Awal", "Keluar", "Masuk", "Saldo Seharusnya", "Total Qty Fisik", "Selisih Saldo", "Kelengkapan"].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={11} className="px-4 py-12 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={11} className="px-4 py-16 text-center">
                    <Scale className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 text-sm">Belum ada data. Impor master obat & lakukan stock opname.</p>
                  </td></tr>
                ) : items.map((m) => {
                  const rows = expanded[m.id_barang];
                  const isOpen = m.id_barang in expanded;
                  return (
                    <React.Fragment key={m.id_barang}>
                      <tr className="border-b border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => toggleExpand(m.id_barang)} data-testid="saldo-row">
                        <td className="px-4 py-3 text-slate-400">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800 max-w-xs">{m.nama}</td>
                        <td className="px-4 py-3 font-mono-t text-xs text-slate-500">{m.id_barang || "-"}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 uppercase">{m.satuan_jual || "-"}</td>
                        <td className="px-4 py-3 text-right tabular font-mono-t text-slate-800">{fmtNum(m.total_awal)}</td>
                        <td className="px-4 py-3 text-right tabular font-mono-t text-red-600">{m.keluar ? fmtNum(m.keluar) : "—"}</td>
                        <td className="px-4 py-3 text-right tabular font-mono-t text-emerald-600">{m.masuk ? fmtNum(m.masuk) : "—"}</td>
                        <td className="px-4 py-3 text-right tabular font-mono-t font-semibold text-slate-800">{fmtNum(m.saldo_seharusnya)}</td>
                        <td className="px-4 py-3 text-right tabular font-mono-t text-slate-800">{fmtNum(m.total_fisik)}</td>
                        <td className="px-4 py-3"><SelisihBadge value={m.selisih} /></td>
                        <td className="px-4 py-3">
                          {m.all_counted ? (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> Lengkap</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500"><CircleDashed className="h-3.5 w-3.5" /> {m.counted_locations}/{m.total_locations} tempat</span>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-slate-50/60">
                          <td colSpan={11} className="px-6 py-3">
                            {rows === null ? (
                              <div className="text-slate-400 text-xs"><Loader2 className="h-4 w-4 animate-spin inline" /> Memuat rincian tempat...</div>
                            ) : (
                              <div className="space-y-1.5">
                                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Rincian per Tempat Penyimpanan</p>
                                {rows.map((r) => (
                                  <div key={r.id} className="flex items-center justify-between text-xs bg-white rounded-lg border border-slate-100 px-3 py-2" data-testid="saldo-detail-row">
                                    <span className="flex items-center gap-1.5 text-slate-600"><MapPin className="h-3.5 w-3.5 text-slate-400" /> {r.lokasi_penyimpanan || "-"}</span>
                                    <div className="flex items-center gap-4 tabular font-mono-t">
                                      <span className="text-slate-500">Awal: <b className="text-slate-700">{fmtNum(r.quantity_awal)}</b></span>
                                      <span className="text-slate-500">Fisik: <b className="text-slate-700">{r.quantity_fisik === null ? "—" : fmtNum(r.quantity_fisik)}</b></span>
                                      <SelisihBadge value={r.selisih} />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Halaman {page + 1} dari {totalPages} · {fmtNum(total)} obat</span>
              <div className="flex gap-2">
                <button disabled={page === 0} onClick={() => { const p = page - 1; setPage(p); load(p, search, status); }} className="p-1.5 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft className="h-4 w-4" /></button>
                <button disabled={page + 1 >= totalPages} onClick={() => { const p = page + 1; setPage(p); load(p, search, status); }} className="p-1.5 rounded-md border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
